import { useEffect, useId, useRef } from 'react';
import type { RouteLine } from '../../types/climb';
import {
  LINE_OPACITY,
  corridorWidthPx,
  hasCorridor,
  isDrawableLine,
  lineThickness,
  routeLineMaskUrl,
  routeLinePath,
} from './route-line';

interface RouteLineOverlayProps {
  line?: RouteLine | null;
  /** Natural (intrinsic) dimensions of the underlying image, from img.onLoad. */
  naturalWidth: number | null;
  naturalHeight: number | null;
  /**
   * Must match the object-fit of the underlying <img>, exactly as in
   * HoldsOverlay: the path and the focus mask both mirror it, so the line
   * stays on the wall it was drawn against at any rendered size.
   */
  objectFit?: 'cover' | 'contain';
  /** Set the wall outside the corridor back. Defaults to true. */
  focusOutside?: boolean;
  /**
   * How that setting-back is done.
   *
   * `desaturate` drains the colour, which is the right cue on a gym wall: the
   * holds of a route are told apart by colour in the first place, so greying
   * the rest answers "which of these are mine" without covering any of them up.
   *
   * `dim` darkens instead. On rock there is no colour to drain — a grey cliff
   * greyed out looks exactly like a grey cliff — so anywhere the corridor
   * itself is the thing being read (the editors, where a slider sets it) this
   * is the one that can actually be seen.
   */
  focusStyle?: 'desaturate' | 'dim';
  /**
   * Draw the green start and red anchor discs. Off for the editors, which draw
   * their own vertices in the same colours at handle size — two sets of markers
   * on the same two points, one of them twice the size of the other, reads as
   * the ends being somehow bigger than the rest of the line.
   */
  showEndMarkers?: boolean;
  /**
   * Draw the line itself. Turning it off while `focusOutside` stays on gives the
   * focus-only reading: the wall narrowed to the route's corridor, with its own
   * holds left unmarked to be worked out.
   */
  showLine?: boolean;
  /** Play the "drawing the line" animation. Defaults to true. */
  animate?: boolean;
  /** How thick to draw the line and its end markers. Defaults to 'full'. */
  marks?: RouteLineMarkSize;
  className?: string;
}

const DRAW_DURATION_MS = 900;

/**
 * How big the marks are drawn, as fractions of the image's long edge.
 *
 * `full` is tuned for a photo shown at page size, where a fine line is enough
 * to trace the route without drawing over it. That same fraction on a thumbnail
 * resolves to well under a pixel — the line disappears and only the corridor is
 * left to read — so `compact` trades proportion for being visible at all.
 */
const MARK_SIZES = {
  full: { line: 0.005, marker: 0.016 },
  compact: { line: 0.02, marker: 0.03 },
} as const;

export type RouteLineMarkSize = keyof typeof MARK_SIZES;

/**
 * Whether the browser can filter what is behind an element. Without it the
 * focus effect falls back to dimming, which any browser can do with a
 * translucent background — losing the colour cue but not the corridor.
 */
const SUPPORTS_BACKDROP_FILTER =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  (CSS.supports('backdrop-filter', 'grayscale(1)') || CSS.supports('-webkit-backdrop-filter', 'grayscale(1)'));

/**
 * Draws the line of a route over its image: a path from the start hold to the
 * anchor, with the wall around it drained to black and white so the route's own
 * band of the picture is the only part still in colour.
 *
 * This is the route's counterpart to HoldsOverlay and shares its contract —
 * fractional coordinates, the image's natural size as the viewBox, the same fit
 * rule as the image. A boulder is read as a set of circled holds; a route is
 * shot from the ground, where the holds are too small to tell apart and too many
 * to circle, so what it needs shown is where the line goes.
 *
 * Colour is the right cue for it because the holds of a route are told apart by
 * colour in the first place: greying out the rest of the wall answers "which of
 * these are mine" without covering any of them up, which darkening did.
 *
 * The effect is a `backdrop-filter` layer rather than a filtered copy of the
 * photo, which means it needs no image URL and works over anything behind it —
 * the <img> on a profile, and equally the editor's canvas while the line is
 * still being drawn.
 */
export function RouteLineOverlay({
  line,
  naturalWidth,
  naturalHeight,
  objectFit = 'cover',
  focusOutside = true,
  focusStyle = 'desaturate',
  showLine = true,
  showEndMarkers = true,
  animate = true,
  marks = 'full',
  className,
}: RouteLineOverlayProps) {
  const revealRef = useRef<SVGPathElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);
  const rawId = useId();
  // useId's colons are not valid in a url(#…) reference.
  const id = rawId.replace(/:/g, '');

  const W = naturalWidth ?? 0;
  const H = naturalHeight ?? 0;
  const drawable = isDrawableLine(line) && W > 0 && H > 0;
  const pointCount = line?.points.length ?? 0;

  useEffect(() => {
    if (!drawable) return;

    const reveal = revealRef.current;
    const focus = focusRef.current;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    // jsdom and reduced-motion: show the finished drawing, no sweep.
    const canAnimate = animate && !reduceMotion;

    if (reveal) {
      reveal.getAnimations?.().forEach((a) => a.cancel());
      if (!canAnimate || typeof reveal.animate !== 'function') {
        reveal.style.strokeDashoffset = '0';
      } else {
        reveal.animate(
          [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
          { duration: DRAW_DURATION_MS, easing: 'ease-in-out', fill: 'forwards' }
        );
      }
    }

    if (focus) {
      focus.getAnimations?.().forEach((a) => a.cancel());
      if (!canAnimate || typeof focus.animate !== 'function') {
        focus.style.opacity = '1';
      } else {
        // The colour drains only once the line has been traced, so the viewer
        // sees where the route goes before the rest of the wall greys out. With
        // no line to trace there is nothing to wait for.
        focus.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 400, delay: showLine ? DRAW_DURATION_MS * 0.7 : 0, easing: 'ease-out', fill: 'forwards' }
        );
      }
    }
  }, [drawable, animate, focusOutside, showLine, pointCount, W, H]);

  if (!drawable) return null;

  const d = routeLinePath(line.points, W, H);
  const longEdge = Math.max(W, H);
  // Whatever the line was drawn at. Thin by default: indoors the wall going
  // black and white is what says where the route is, so the line only traces
  // it. A crag's line carries the reading on its own and is often set heavier.
  const lineWidth = longEdge * lineThickness(line, marks);
  const haloWidth = lineWidth * 2.4;
  const markerRadius = longEdge * MARK_SIZES[marks].marker;
  // Wide enough to uncover the halo and the end markers as the sweep passes.
  const revealWidth = Math.max(haloWidth, markerRadius * 2) * 1.8;
  const preserveAspectRatio = objectFit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice';

  const first = line.points[0];
  const last = line.points[line.points.length - 1];
  const maskUrl = routeLineMaskUrl(d, W, H, corridorWidthPx(line.corridor, W, H), preserveAspectRatio);

  return (
    <>
      {/* A line may say it wants no corridor at all (`corridor: 0`), which is
          what a crag topo asks for: the line on the photograph and nothing
          done to the rock around it. Read off the line rather than decided by
          each caller, so every surface that draws it agrees. */}
      {focusOutside && hasCorridor(line) && (
        <div
          ref={focusRef}
          data-testid="route-line-focus"
          className={`absolute inset-0 pointer-events-none ${className ?? ''}`}
          style={{
            opacity: animate ? 0 : 1,
            // The mask's feathered edge is what keeps this from reading as a
            // cut-out: the wall returns gradually as the corridor is reached.
            ...(focusStyle === 'desaturate' && SUPPORTS_BACKDROP_FILTER
              ? { backdropFilter: 'grayscale(1)', WebkitBackdropFilter: 'grayscale(1)' }
              : { backgroundColor: 'rgba(0, 0, 0, 0.5)' }),
            maskImage: maskUrl,
            WebkitMaskImage: maskUrl,
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
          }}
        />
      )}
      {showLine && (
      <svg
        className={`absolute inset-0 w-full h-full pointer-events-none ${className ?? ''}`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio={preserveAspectRatio}
        aria-hidden="true"
        data-testid="route-line-overlay"
      >
        <defs>
          <mask id={`reveal-${id}`}>
            <path
              ref={revealRef}
              d={d}
              stroke="white"
              strokeWidth={revealWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              // pathLength normalizes the path to 1 so the sweep (1 → 0) traces
              // it whatever its real length. Starts fully hidden.
              pathLength={1}
              strokeDasharray={1}
              strokeDashoffset={animate ? 1 : 0}
            />
          </mask>
        </defs>
        <g mask={`url(#reveal-${id})`}>
          {/* The line reads as a hint, not as a drawing over the photo: solid and
              half-transparent, so it stays light over a thumbnail of the wall.
              The markers keep full opacity — they are small, and where the route
              starts and ends is the part worth being definite about. */}
          <g opacity={LINE_OPACITY}>
            {/* Halo first: the line has to stay legible over pale rock and dark resin alike. */}
            <path d={d} stroke="rgba(0,0,0,0.55)" strokeWidth={haloWidth} strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <path d={d} stroke="white" strokeWidth={lineWidth} strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </g>
          {/* Green start, red anchor — the colour semantics holds already use. */}
          {showEndMarkers && (
            <>
              <circle cx={first.x * W} cy={first.y * H} r={markerRadius} fill="#22c55e" stroke="white" strokeWidth={lineWidth * 0.6} />
              <circle cx={last.x * W} cy={last.y * H} r={markerRadius} fill="#ef4444" stroke="white" strokeWidth={lineWidth * 0.6} />
            </>
          )}
        </g>
      </svg>
      )}
    </>
  );
}
